import { useEffect, useState } from 'react';
import { fetchJSON, postJSON } from '../api/client';

interface IFile { file: string; size: number }
interface IAssignment {
  id: number; user_id: number; username: string; file: string;
  page: number; status: string; assigned_at?: string; name?: string;
}
interface IProject {
  folder: string; project_id: string; s3_uri: string;
  files: IFile[]; registered: boolean; assignments: IAssignment[];
}
interface IUser { id: number; username: string; role: string }

const PILL: Record<string, string> = {
  assigned: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-amber-100 text-amber-800',
  completed: 'bg-green-100 text-green-800',
  skipped: 'bg-gray-200 text-gray-600',
};

/** Internal-annotation admin: list S3 `internal_annotation/` datasets (no
 *  pipeline), assign a file to annotator(s), and watch status. Owner-only. */
export function InternalAdmin(): JSX.Element {
  const [projects, setProjects] = useState<IProject[]>([]);
  const [users, setUsers] = useState<IUser[]>([]);
  const [sel, setSel] = useState<Record<string, number>>({});
  const [busy, setBusy] = useState('');
  const [loading, setLoading] = useState(true);

  async function load() {
    setLoading(true);
    const [p, u] = await Promise.all([
      fetchJSON<IProject[]>('/api/internal/projects'),
      fetchJSON<IUser[]>('/api/internal/users'),
    ]);
    if (p) setProjects(p);
    if (u) setUsers(u.filter((x) => x.role === 'annotator'));
    setLoading(false);
  }
  useEffect(() => { load(); }, []);

  async function assign(folder: string, file: string) {
    const key = `${folder}|${file}`;
    const uid = sel[key];
    if (!uid) return;
    setBusy(key);
    await postJSON(`/api/internal/projects/${encodeURIComponent(folder)}/assign`, {
      user_ids: [uid], file,
    });
    setBusy('');
    await load();
  }

  return (
    <div className="mx-auto max-w-5xl p-6">
      <h1 className="text-xl font-semibold">Internal annotation datasets</h1>
      <p className="mb-4 mt-1 text-sm text-gray-500">
        S3 <code>internal_annotation/</code> projects — no triage / research / extraction.
        Assign a file directly to annotators and watch status.
      </p>
      <button onClick={load} className="mb-4 text-sm text-blue-600 underline">Refresh</button>

      {loading && <div className="text-sm text-gray-400">Loading…</div>}
      {!loading && projects.length === 0 && (
        <div className="text-sm text-gray-400">
          No internal projects found under <code>internal_annotation/</code>.
        </div>
      )}

      {projects.map((p) => (
        <div key={p.folder} className="mb-4 rounded border border-gray-200 p-4">
          <div className="flex items-center gap-2">
            <span className="font-medium">{p.folder}</span>
            <span className="rounded bg-purple-100 px-2 py-0.5 text-xs text-purple-800">Internal</span>
            {!p.registered && <span className="text-xs text-gray-400">(registers on first assign)</span>}
          </div>

          {p.files.map((f) => {
            const key = `${p.folder}|${f.file}`;
            return (
              <div key={f.file} className="mt-3 flex items-center gap-2">
                <span className="flex-1 text-sm">
                  {f.file} <span className="text-gray-400">({Math.round(f.size / 1e6)} MB)</span>
                </span>
                <select
                  className="rounded border border-gray-300 px-2 py-1 text-sm"
                  value={sel[key] || ''}
                  onChange={(e) => setSel({ ...sel, [key]: Number(e.target.value) })}
                >
                  <option value="">assign to…</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.username}</option>)}
                </select>
                <button
                  disabled={busy === key || !sel[key]}
                  onClick={() => assign(p.folder, f.file)}
                  className="rounded bg-blue-600 px-3 py-1 text-sm text-white disabled:opacity-40"
                >
                  {busy === key ? '…' : 'Assign'}
                </button>
              </div>
            );
          })}

          {p.assignments.length > 0 && (
            <table className="mt-4 w-full text-xs">
              <thead>
                <tr className="text-left text-gray-400">
                  <th className="pb-1">annotator</th><th>file</th><th>status</th><th>assigned</th>
                </tr>
              </thead>
              <tbody>
                {p.assignments.map((a) => (
                  <tr key={a.id} className="border-t border-gray-100">
                    <td className="py-1">{a.username}</td>
                    <td>{a.file}</td>
                    <td>
                      <span className={`rounded px-2 py-0.5 ${PILL[a.status] || 'bg-gray-100'}`}>
                        {a.status}
                      </span>
                    </td>
                    <td>{a.assigned_at?.slice(0, 16).replace('T', ' ')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}
